// ============================================================
// feature-route.ts
// ============================================================
// Which URLs sit behind a company feature that used to be ungated, and
// what a route should do while that check is in flight.
//
// daily_logs, par_sheets, my_agreements, employee_files, and
// brand_standards were plain nav rows. They now resolve through
// has_feature_access (plan JSON, then a company_feature_overrides row).
// The plan backfill that keeps them ON for existing companies lives in
// docs/proposed-migrations/ and is not applied by this change. An override
// row with enabled = false is what turns one company off.
//
// 'loading' must not redirect. useFeatureAccess starts closed, so treating
// an in-flight check as "off" bounces every tenant to the dashboard for a
// frame on each cold load.

export type FeatureRouteDecision = 'loading' | 'redirect' | 'allow';

export function featureRouteDecision(state: {
  hasAccess: boolean;
  loading: boolean;
}): FeatureRouteDecision {
  if (state.loading) return 'loading';
  if (!state.hasAccess) return 'redirect';
  return 'allow';
}

/** Where a denied web route lands. Kiosk denials use the limited dashboard. */
export const FEATURE_OFF_REDIRECT = '/dashboard';
export const KIOSK_FEATURE_OFF_REDIRECT = '/limited/dashboard';

/**
 * Page-level feature for a path, ignoring query strings. Longest prefix
 * wins so /limited/par-sheets is par_sheets and not a shorter sibling.
 * Legacy redirects (/temperature-logs, /shift-checkins, /restroom-checks)
 * land on the Daily Logs hub, so they share daily_logs.
 */
const FEATURE_PATHS: ReadonlyArray<{ prefix: string; feature: string }> = [
  { prefix: '/limited/temperature-logs', feature: 'daily_logs' },
  { prefix: '/limited/restroom-checks', feature: 'daily_logs' },
  { prefix: '/limited/log-consumption', feature: 'daily_logs' },
  { prefix: '/limited/shift-checkins', feature: 'daily_logs' },
  { prefix: '/limited/par-sheets', feature: 'par_sheets' },
  { prefix: '/limited/agreements', feature: 'my_agreements' },
  { prefix: '/daily-logs', feature: 'daily_logs' },
  { prefix: '/temperature-logs', feature: 'daily_logs' },
  { prefix: '/restroom-checks', feature: 'daily_logs' },
  { prefix: '/shift-checkins', feature: 'daily_logs' },
  { prefix: '/par-sheets', feature: 'par_sheets' },
  { prefix: '/my-agreements', feature: 'my_agreements' },
  { prefix: '/employee-files', feature: 'employee_files' },
  { prefix: '/brand-standards', feature: 'brand_standards' },
];

export function featureKeyForPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const bare = path.split('?')[0].split('#')[0];
  let match: { prefix: string; feature: string } | null = null;
  for (const entry of FEATURE_PATHS) {
    const hit =
      bare === entry.prefix || bare.startsWith(`${entry.prefix}/`);
    if (!hit) continue;
    if (!match || entry.prefix.length > match.prefix.length) match = entry;
  }
  return match?.feature ?? null;
}
